import { has, partial } from 'lodash-es';
import { from, tap } from 'rxjs';
import { temporal } from 'zundo';
import { createWithEqualityFn } from 'zustand/traditional';

import { TrackIDManager } from '@/manager';
import type {
  BoxClipboardStoreActions,
  BoxClipboardStoreState,
  BoxWithLabel,
} from '@/type';

const isBoxWithLabel = (value: Dictionary): value is BoxWithLabel => {
  const hasProp = partial(has, value);
  const hasPropIsArray = (prop: string) =>
    hasProp(prop) && Array.isArray(value[prop]) && value[prop].length >= 3;

  return (
    hasProp('label') &&
    hasPropIsArray('position') &&
    hasPropIsArray('dimension') &&
    hasPropIsArray('rotation')
  );
};

const fetchInitClipboardData = async () => {
  try {
    const data = await navigator.clipboard.readText();
    const jsonData = JSON.parse(data) as object;
    const clipboardData: {
      boxList: BoxWithLabel[];
      trackID: string | null;
    } = {
      boxList: [],
      trackID: null,
    };

    if (Array.isArray(jsonData)) {
      const prevCopiedBoxList = jsonData.filter(isBoxWithLabel);

      if (prevCopiedBoxList.length) {
        clipboardData.boxList = prevCopiedBoxList;
      }
    } else if (TrackIDManager.isValidTrackID(data)) {
      clipboardData.trackID = data;
    }

    return clipboardData;
  } catch (err) {
    console.group('[fetchInitClipboardData] error');
    console.warn(err);
    console.groupEnd();
    return;
  }
};

const useBoxClipboardStore = createWithEqualityFn(
  temporal<BoxClipboardStoreState & BoxClipboardStoreActions>(
    (set, get) => {
      const getLength = () => get().copiedBoxList.length;

      const getLast = () =>
        JSON.parse(get().copiedBoxList[getLength() - 1] ?? '[]');

      from(fetchInitClipboardData())
        .pipe(
          tap(clipboardData => {
            if (clipboardData?.boxList.length) {
              get().copy(clipboardData.boxList);
            }

            if (clipboardData?.trackID) {
              get().copyTrackID(clipboardData.trackID);
            }
          }),
        )
        .subscribe();

      return {
        copiedBoxList: [],
        copiedTrackID: null,

        copy(boxList) {
          if (!Array.isArray(boxList)) return get().copy([boxList]);

          set({
            copiedBoxList: [...get().copiedBoxList, JSON.stringify(boxList)],
          });

          navigator.clipboard.writeText(JSON.stringify(boxList));

          return Promise.resolve(true);
        },

        copyTrackID(trackID) {
          if (!TrackIDManager.isValidTrackID(trackID))
            return Promise.resolve(false);

          set({
            copiedTrackID: trackID,
          });

          navigator.clipboard.writeText(trackID);

          return Promise.resolve(true);
        },

        paste() {
          return Promise.resolve(getLast());
        },

        pasteTrackID() {
          const trackID = get().copiedTrackID;

          return Promise.resolve(trackID);
        },
      };
    },
    {
      limit: 20,
    },
  ),
);

export default useBoxClipboardStore;
